import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Trophy, Bot, FileSearch, Percent, Users,
  UserCircle, AlertTriangle, TrendingUp, TrendingDown, ChevronRight,
  Search, Bell, Send, Medal, CheckCircle2, Circle, Sparkles, LogOut,
  Home, Star, Rss, Settings, Calculator,
} from "lucide-react";
import "./MeuCockpitDashboard.css";
import { api } from "../utils/api";
import { useAuth } from "../context/AuthContext";

// ---- PTAX completa do Banco Central ----
const API_PTAX = "https://olinda.bcb.gov.br/olinda/servico/PTAX/versao/v1/odata/CotacaoMoedaDia(moeda=@moeda,dataCotacao=@dataCotacao)";
const pad = n => String(n).padStart(2,"0");
const toMMDDYYYY = d => `${pad(d.getMonth()+1)}-${pad(d.getDate())}-${d.getFullYear()}`;
const toBR = d => `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
function nextBizDay(d) { let r=new Date(d); r.setDate(r.getDate()+1); while(r.getDay()===0||r.getDay()===6) r.setDate(r.getDate()+1); return r; }
function prevBizDay(d) { let r=new Date(d); r.setDate(r.getDate()-1); while(r.getDay()===0||r.getDay()===6) r.setDate(r.getDate()-1); return r; }

async function fetchQuote(moeda, dateObj) {
  const url = `${API_PTAX}?@moeda='${moeda}'&@dataCotacao='${toMMDDYYYY(dateObj)}'&$format=json&$select=cotacaoCompra,cotacaoVenda`;
  const r = await fetch(url); if(!r.ok) return null;
  const d = await r.json();
  return d.value?.length ? d.value[d.value.length-1] : null;
}

async function findPrevQuote(moeda, ref) {
  let cursor = prevBizDay(ref);
  for(let i=0;i<10;i++) {
    const q = await fetchQuote(moeda, cursor);
    if(q) return { quote:q, date:cursor };
    cursor = prevBizDay(cursor);
  }
  return null;
}


async function fetchHistory(moeda, days=10) {
  const history = [];
  let cursor = new Date();
  let found = 0;
  while(found < days) {
    cursor = prevBizDay(cursor);
    const q = await fetchQuote(moeda, cursor);
    if(q) { history.unshift({ date: toBR(cursor), venda: parseFloat(q.cotacaoVenda) }); found++; }
    if(found > 30) break;
  }
  return history;
}

function usePTAX() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const MOEDAS = [
      { code:"USD", label:"Dólar", flag:"🇺🇸" },
      { code:"EUR", label:"Euro", flag:"🇪🇺" },
      { code:"GBP", label:"Libra", flag:"🇬🇧" },
    ];
    const today = new Date();
    const next = nextBizDay(today);
    Promise.all(MOEDAS.map(async ({ code, label, flag }) => {
      try {
        const prev = await findPrevQuote(code, today);
        const fresh = await fetchQuote(code, today);
        if(!prev) return null;
        return {
          label, flag, code,
          venda: parseFloat(prev.quote.cotacaoVenda),
          compra: parseFloat(prev.quote.cotacaoCompra),
          dataPrev: toBR(prev.date),
          dataProx: toBR(next),
          vendaProx: fresh ? parseFloat(fresh.cotacaoVenda) : null,
        };
      } catch { return null; }
    })).then(rs => { setRates(rs.filter(Boolean)); setLoading(false); });
  }, []);
  return { rates, loading };
}

// ---- NavGroups dinâmicos ----
function makeNavGroups(navigate, logout, user) {
  return [
    {
      label: "Meu progresso",
      items: [
        { label: "Dashboard", icon: Home, path: "/dashboard" },
        { label: "Minhas Operações", icon: Package, path: "/operacoes" },
        { label: "Conquistas", icon: Trophy, path: "/conquistas" },
      ],
    },
    {
      label: "Ferramentas",
      items: [
        { label: "ImportBot IA", icon: Bot, path: "/ia" },
        { label: "Consulta NCM", icon: FileSearch, path: "/ncm" },
        { label: "Taxa Siscomex", icon: Percent, path: "/taxa-siscomex" },
        { label: "Fornecedores", icon: Users, path: "/fornecedores" },
      ],
    },
    {
      label: "Comunidade",
      items: [{ label: "Feed da Turma", icon: Rss, path: "/feed" }],
    },
    {
      label: "Conta",
      items: [{ label: "Meu Perfil", icon: UserCircle, path: "/perfil" }],
    },
  ];
}

function StatCard({ label, value, icon: Icon, accent, onClick }) {
  return (
    <div className="mc-card" onClick={onClick} style={{cursor:onClick?"pointer":"default"}}
      onMouseEnter={e=>onClick&&(e.currentTarget.style.transform="translateY(-2px)")}
      onMouseLeave={e=>onClick&&(e.currentTarget.style.transform="none")}>
      <div className="mc-stat-top">
        <p style={{fontSize:11,fontWeight:600,color:"#94A3B8",textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>{label}</p>
        <div className="mc-stat-icon" style={{backgroundColor:`${accent}18`,borderRadius:10,padding:8}}>
          <Icon size={18} color={accent} />
        </div>
      </div>
      <p style={{fontSize:34,fontWeight:800,color:"#0F172A",letterSpacing:"-.02em",margin:"8px 0 0"}}>{value}</p>
    </div>
  );
}

function SectionCard({ icon: Icon, title, action, children, style }) {
  return (
    <div className="mc-card" style={style}>
      <div className="mc-card-header">
        <div className="mc-card-header-left">
          <Icon size={16} color="#0057FF" />
          <p style={{fontSize:15,fontWeight:700,color:"#0F172A",letterSpacing:"-.01em"}}>{title}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function JornadaCard({ operacoes, alertas, navigate }) {
  const atrasadas = new Set(alertas.map(a => a.operacao_id));
  return (
    <SectionCard icon={Package} title="Jornada da Importação"
      action={
        <button className="mc-card-action" onClick={() => navigate("/operacoes")}>
          Ver todas <ChevronRight size={14} />
        </button>
      }>
      <div className="mc-process-list">
        {operacoes.length === 0 && (
          <div style={{textAlign:"center",padding:24,color:"#5B6B85",fontSize:13}}>
            Nenhuma operação ainda.{" "}
            <button onClick={() => navigate("/operacoes")} style={{color:"#0098DA",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>Criar →</button>
          </div>
        )}
        {operacoes.slice(0, 5).map(op => {
          const pct = parseInt(op.pct || 0);
          const done = pct === 100;
          const atrasada = atrasadas.has(op.id);
          const cor = done ? "#22C55E" : atrasada ? "#EF4444" : "#0098DA";
          return (
            <div className="mc-process-row" key={op.id}
              style={{cursor:"pointer"}}
              onClick={() => navigate(`/operacoes/${op.id}`)}>
              {done ? <CheckCircle2 size={18} color="#22C55E" /> : atrasada ? <AlertTriangle size={18} color="#EF4444" /> : <Circle size={18} color="#0098DA" />}
              <div className="mc-process-body">
                <div className="mc-process-top">
                  <span className="mc-process-name">{op.nome}</span>
                  <span className="mc-process-id mc-mono">{pct}%</span>
                </div>
                <div style={{marginTop:6}}>
                  <div style={{display:"flex",gap:2,marginBottom:4}}>
                    {[1,2,3,4,5,6,7,8].map(f => {
                      const fPct = pct >= f*12.5 ? 100 : pct > (f-1)*12.5 ? 50 : 0;
                      const fCor = fPct===100?cor:fPct===50?`${cor}88`:"#E2E8F0";
                      return <div key={f} style={{flex:1,height:4,borderRadius:2,background:fCor,transition:".4s"}} />;
                    })}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:10,color:atrasada?"#EF4444":done?"#22C55E":"#0057FF",fontWeight:600}}>
                      {done ? "✓ Concluído" : atrasada ? `⚠️ Fase ${Math.ceil(pct/12.5)||1} vencida` : `▶ Fase ${Math.ceil(pct/12.5)||1} — ${["Produto","Classif.","Fornec.","Custos","Amostras","Importação","Desembaraço","Pós-Import."][Math.max(0,Math.ceil(pct/12.5)-1)]}`}
                    </span>
                    <span style={{fontSize:10,color:"#94A3B8"}}>{op.tarefas_feitas}/{op.total_tarefas} tarefas</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

function PtaxCard({ rates, loading }) {
  return (
    <SectionCard icon={Percent} title="Cotação PTAX — Siscomex">
      {loading && <div style={{color:"#94A3B8",fontSize:13,padding:"8px 0"}}>Carregando cotações...</div>}
      <div style={{display:"grid",gridTemplateColumns:"auto 1fr 1fr",gap:"6px 12px",alignItems:"center"}}>
        <div style={{fontSize:10,fontWeight:600,color:"#94A3B8",textTransform:"uppercase",letterSpacing:".06em"}}></div>
        <div style={{fontSize:10,fontWeight:600,color:"#94A3B8",textTransform:"uppercase",letterSpacing:".06em"}}>Hoje</div>
        <div style={{fontSize:10,fontWeight:600,color:"#0057FF",textTransform:"uppercase",letterSpacing:".06em"}}>Amanhã</div>
        {rates.map(r => {
          const up = r.history?.length > 1 && r.history[r.history.length-1].venda >= r.history[0].venda;
          return (
          <>
            <div key={r.code+"label"} style={{display:"flex",alignItems:"center",gap:6,paddingTop:6}}>
              <span style={{fontSize:16}}>{r.flag}</span>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:"#0F172A"}}>{r.label}</div>
                <div style={{fontSize:10,color:"#94A3B8",fontFamily:"monospace"}}>{r.code}</div>
              </div>
            </div>
            <div key={r.code+"hoje"} style={{paddingTop:6}}>
              <div style={{fontSize:16,fontWeight:700,color:"#0F172A",fontFamily:"monospace"}}>{r.venda.toFixed(4)}</div>
              <div style={{fontSize:10,color:"#94A3B8"}}>{r.dataPrev}</div>
            </div>
            <div key={r.code+"amanha"} style={{paddingTop:6}}>
              {r.vendaProx ? (
                <>
                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                    <div style={{fontSize:16,fontWeight:700,color:"#0057FF",fontFamily:"monospace"}}>{r.vendaProx.toFixed(4)}</div>
                    <span style={{fontSize:10,color:up?"#22C55E":"#EF4444",fontWeight:600}}>{up?"▲":"▼"}</span>
                  </div>
                  <div style={{fontSize:10,color:"#94A3B8"}}>{r.dataProx}</div>
                </>
              ) : <div style={{fontSize:11,color:"#94A3B8"}}>—</div>}
              {r.history && <div style={{marginTop:4}}><MiniChart data={r.history} color={r.code} /></div>}
            </div>
          </>
          );
        })}
      </div>
    </SectionCard>
  );
}

function FerramentasCard({ navigate }) {
  const ferramentas = [
    { icon: Bot, label: "ImportBot IA", sub: "Classifique e calcule custos", path: "/ia", color: "#0057FF", bg: "#EEF4FF" },
    { icon: FileSearch, label: "Consulta NCM", sub: "TEC oficial + alíquotas + TA", path: "/ncm", color: "#8B5CF6", bg: "#F5F3FF" },
    { icon: Percent, label: "Taxa Siscomex", sub: "PTAX e câmbio oficial", path: "/taxa-siscomex", color: "#10B981", bg: "#ECFDF5" },
    { icon: Users, label: "Fornecedores", sub: "Base de fornecedores aprovados", path: "/fornecedores", color: "#F59E0B", bg: "#FFFBEB" },
    { icon: Calculator, label: "Custos da Importação", sub: "Simulador completo de custos", path: "/ferramentas/simulador-custos.html", color: "#EC4899", bg: "#FDF2F8", externo: true },
  ];
  return (
    <div style={{background:"#fff",borderRadius:16,padding:"22px 24px",border:"1px solid rgba(0,87,255,.08)",boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}>
      <div style={{fontSize:15,fontWeight:700,color:"#0F172A",marginBottom:16,letterSpacing:"-.01em"}}>🛠️ Ferramentas</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
        {ferramentas.map(f => (
          <div key={f.path} onClick={() => f.externo ? window.open(f.path, "_blank") : navigate(f.path)}
            style={{padding:"20px 18px",borderRadius:14,background:f.bg,border:`1px solid ${f.color}22`,cursor:"pointer",transition:".2s"}}
            onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=`0 6px 20px ${f.color}22`;}}
            onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
            <f.icon size={24} color={f.color} />
            <div style={{fontSize:14,fontWeight:700,color:"#0F172A",marginTop:10}}>{f.label}</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:3}}>{f.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RankingCard({ ranking, userId }) {
  const medalColor = { 0: "#F59E0B", 1: "#94A3B8", 2: "#CD7F32" };
  return (
    <SectionCard icon={Trophy} title="Ranking da Turma">
      <div style={{display:"flex",flexDirection:"column",gap:4}}>
        {ranking.length === 0 && <div style={{color:"#5B6B85",fontSize:13}}>Nenhum dado ainda.</div>}
        {ranking.slice(0, 10).map((r, i) => {
          const eu = String(r.id) === String(userId);
          return (
            <div key={r.id} style={{
              display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:10,
              background: eu ? "linear-gradient(135deg,#EEF4FF,#DCE8FF)" : "#F8FAFC",
              border: `1px solid ${eu?"rgba(0,87,255,.2)":"#F1F5F9"}`,
            }}>
              <div style={{width:22,height:22,borderRadius:6,fontSize:11,fontWeight:700,
                background:i===0?"#F59E0B":i===1?"#94A3B8":i===2?"#CD7F32":"#E2E8F0",
                color:i<3?"#fff":"#64748B",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {i+1}
              </div>
              <div style={{flex:1,fontSize:12,fontWeight:eu?700:400,color:eu?"#0057FF":"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {r.nome}
              </div>
              <div style={{fontSize:11,fontWeight:700,color:"#F59E0B",fontFamily:"monospace",flexShrink:0}}>
                {parseFloat(r.pontuacao).toFixed(0)}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

export default function Dashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [operacoes, setOperacoes] = useState([]);
  const [conquistas, setConquistas] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [feedNovos, setFeedNovos] = useState(0);
  const { rates: ptaxRates, loading: ptaxLoading } = usePTAX();

  useEffect(() => {
    api.get("/operacoes", token).then(setOperacoes);
    api.get("/conquistas", token).then(setConquistas);
    api.get("/operacoes/alertas", token).then(setAlertas).catch(() => {});
    api.get("/operacoes/meu-ranking", token).then(setRanking).catch(() => {});
    api.get("/feed", token).then(posts => {
      const u = localStorage.getItem("feed_visto") || "0";
      setFeedNovos(posts.filter(p => new Date(p.criado_em).getTime() > parseInt(u)).length);
    }).catch(() => {});
  }, [token]);

  const totalPontos = conquistas.reduce((soma, c) => soma + (parseInt(c.vezes_conquistada, 10) || 0), 0) * 12.5;
  const melhorOp = operacoes.length > 0 ? Math.max(...operacoes.map(o => parseInt(o.pct) || 0)) : 0;
  const minhaPos = ranking.findIndex(r => String(r.id) === String(user?.id)) + 1;

  const hora = new Date().getHours();
  const saudacao = hora < 12 ? "Bom dia" : hora < 18 ? "Boa tarde" : "Boa noite";

  return (
    <div className="mc-main" style={{flex:1}}>
        <div className="mc-topbar">
          <div>
            <h1 className="mc-greeting" style={{color:"#0F172A"}}>{saudacao}, {user?.nome?.split(" ")[0]}! 👋</h1>
            <p className="mc-greeting-sub" style={{color:"#64748B"}}>Seu Centro de Controle da Importação</p>
          </div>
          <div className="mc-topbar-right">

            {alertas.length > 0 && (
              <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.2)",borderRadius:10,padding:"8px 12px"}}>
                <AlertTriangle size={14} color="#EF4444" />
                <span style={{fontSize:12,fontWeight:600,color:"#EF4444"}}>{alertas.length} prazo{alertas.length>1?"s":""} vencido{alertas.length>1?"s":""}</span>
              </div>
            )}
            <button className="mc-icon-btn">
              <Bell size={16} color="#94A3B8" />
              {feedNovos > 0 && <span className="mc-badge-count">{feedNovos}</span>}
            </button>
            <div className="mc-avatar" onClick={() => navigate("/perfil")} style={{cursor:"pointer"}}>
              <span style={{fontSize:13,fontWeight:700,color:"#94A3B8"}}>
                {user?.nome?.split(" ").map(n=>n[0]).slice(0,2).join("").toUpperCase()||"?"}
              </span>
            </div>
          </div>
        </div>

        <div className="mc-content">
          {/* Stats */}
          <div className="mc-stats-grid" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
            <StatCard label="Operações Ativas" value={String(operacoes.length)} icon={Package} accent="#0057FF" onClick={()=>navigate('/operacoes')} />
            <StatCard label="Melhor Resultado" value={melhorOp + "%"} icon={TrendingUp} accent="#22C55E" />
            <StatCard label="Minha Pontuação" value={totalPontos.toFixed(0) + "pts"} icon={Sparkles} accent="#A78BFA" onClick={()=>navigate('/conquistas')} />
            {/* Posição no Ranking oculta até haver base de alunos suficiente — lógica de minhaPos mantida abaixo */}
          </div>

          {/* Jornada + PTAX */}
          <div className="mc-row-2col" style={{alignItems:"start"}}>
            <JornadaCard operacoes={operacoes} alertas={alertas} navigate={navigate} />
            <PtaxCard rates={ptaxRates} loading={ptaxLoading} />
          </div>

          {/* Ferramentas — full width */}
          <FerramentasCard navigate={navigate} />
        </div>
    </div>
  );
}
